import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, PaperPlaneTilt, ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { REFER, GALLERY } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border border-black/10 bg-white px-5 py-3.5 text-sm text-brand-ink placeholder:text-neutral-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-brand focus:shadow-[0_0_0_3px_rgba(1,86,207,0.12)]";

const Label = ({ children }) => (
  <label className="mb-2 block text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-brand-ink/70">{children}</label>
);

const ReferFriend = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", friendName: "", friendContact: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        topic: "Refer a friend",
        message: `Referral from ${form.name}.\n\nFriend's name: ${form.friendName}\nFriend's contact: ${form.friendContact}\n\nNotes: ${form.notes || "—"}`,
      });
      setSent(true);
      toast.success("Thank you — your referral is on its way.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="refer-friend-page" className="bg-brand-bone">
      <Seo
        title="Refer a Friend — £100 Voucher"
        description="Refer a friend to BTK Mortgage Services. When their mortgage completes, you receive a £100 John Lewis or Amazon voucher."
        path="/resources/refer-a-friend"
        image={GALLERY[0]}
      />
      <PageHero
        testId="refer-hero"
        eyebrow="Refer a friend"
        title="Know someone"
        italic="buying or moving?"
        intro={REFER.intro}
        image={GALLERY[0]}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow text-brand"><span className="rule-line" />How it works</p>
              <h2 className="editorial-h mt-6 text-3xl text-brand-ink sm:text-4xl">
                Three simple <span className="italic text-brand">steps.</span>
              </h2>
            </Reveal>
            <div className="mt-10 space-y-5">
              {REFER.steps.map(([title, text], i) => (
                <Reveal key={title} delay={i * 0.08}>
                  <div data-testid={`refer-step-${i + 1}`} className="flex gap-6 border-l-2 border-brand/30 bg-white p-7">
                    <span aria-hidden className="editorial-h shrink-0 text-4xl text-brand/30">
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="editorial-h text-xl text-brand-ink">{title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-neutral-600">{text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              {sent ? (
                <div data-testid="refer-success-panel" className="border border-black/10 bg-white p-10 shadow-[0_28px_60px_rgba(10,19,38,0.08)] sm:p-14">
                  <span className="inline-flex h-14 w-14 items-center justify-center border border-brand/30 text-brand">
                    <Gift size={26} weight="duotone" />
                  </span>
                  <h2 className="editorial-h mt-8 text-3xl text-brand-ink">Thank you for the referral</h2>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600">
                    We'll reach out to your friend for a friendly, no-pressure chat — and we'll look
                    after them properly. When their mortgage completes, your £100 John Lewis or
                    Amazon voucher follows.
                  </p>
                  <button
                    data-testid="refer-send-another-btn"
                    onClick={() => {
                      setSent(false);
                      setForm({ name: "", email: "", phone: "", friendName: "", friendContact: "", notes: "" });
                    }}
                    className="mt-10 inline-flex items-center gap-2 border border-brand-ink/20 px-6 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-brand-ink transition-[border-color,color] duration-200 hover:border-brand hover:text-brand"
                  >
                    Refer someone else
                    <ArrowRight size={13} weight="bold" />
                  </button>
                </div>
              ) : (
                <form
                  data-testid="refer-form"
                  onSubmit={submit}
                  className="border border-black/10 bg-white p-8 shadow-[0_28px_60px_rgba(10,19,38,0.08)] sm:p-12"
                >
                  <p className="eyebrow text-brand"><span className="rule-line" />Referral form</p>
                  <h2 className="editorial-h mt-5 text-3xl text-brand-ink">Your details</h2>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div>
                      <Label>Your name</Label>
                      <input required minLength={2} data-testid="refer-name-input" className={inputClass} placeholder="Jane Smith" value={form.name} onChange={set("name")} />
                    </div>
                    <div>
                      <Label>Your email</Label>
                      <input required type="email" data-testid="refer-email-input" className={inputClass} placeholder="jane@example.com" value={form.email} onChange={set("email")} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Your phone (optional)</Label>
                      <input type="tel" data-testid="refer-phone-input" className={inputClass} placeholder="07..." value={form.phone} onChange={set("phone")} />
                    </div>
                  </div>
                  <h2 className="editorial-h mt-12 text-3xl text-brand-ink">Your friend's details</h2>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div>
                      <Label>Friend's name</Label>
                      <input required minLength={2} data-testid="refer-friend-name-input" className={inputClass} placeholder="Alex Jones" value={form.friendName} onChange={set("friendName")} />
                    </div>
                    <div>
                      <Label>Friend's email or phone</Label>
                      <input required data-testid="refer-friend-contact-input" className={inputClass} placeholder="alex@example.com" value={form.friendContact} onChange={set("friendContact")} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Anything we should know? (optional)</Label>
                      <textarea rows={4} data-testid="refer-notes-input" className={`${inputClass} resize-y`} placeholder="e.g. They're first-time buyers looking in Surbiton…" value={form.notes} onChange={set("notes")} />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="refer-submit-btn"
                    className="mt-10 inline-flex items-center gap-2 bg-brand px-8 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.22em] text-white shadow-[0_14px_40px_rgba(1,86,207,0.35)] transition-[background-color,transform,opacity] duration-200 hover:bg-brand-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {sending ? "Sending…" : "Send the referral"}
                    <PaperPlaneTilt size={14} weight="bold" />
                  </button>
                  <p className="mt-6 text-[11px] leading-relaxed text-neutral-400">
                    Please make sure your friend is happy for us to contact them before sending their
                    details. We'll mention your name when we get in touch.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ReferFriend;
